import { createFilterOptions } from '@material-ui/lab';
import { FormikProps } from 'formik';
import React from 'react';
import { z } from 'zod';

import Autocomplete from 'src/components/Autocomplete';
import { getGroupOptionMapper } from 'src/constants/multiSelectConsts';
import { useAppSelector } from 'src/hooks/useStore';
import {
  GroupListboxComponent,
  RenderGroup,
} from 'src/legacy/components/Select/GroupListBox';
import { Company } from 'src/store/clients/types';

import { SelectCompanyAutocompleteItem } from './SelectCompanyAutocompleteItem';
import { SelectCompanyTextInput } from './SelectCompanyTextInput';
import { CompanyAutocompleteItem } from './types';

type OptionalFormikProps<Fields extends Record<string, unknown>> = Partial<
  Pick<FormikProps<Fields>, 'touched' | 'errors'>
>;

interface SelectCompanyProps<Fields extends Record<string, unknown>>
  extends OptionalFormikProps<Fields> {
  placeholder?: string;
  autoFocus?: boolean;
  excludeCompanyIds?: string[];
  label?: React.ReactNode;
  onChange: (item: CompanyAutocompleteItem | null) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  selectedCompanyId?: string;
}

const filter = createFilterOptions<CompanyAutocompleteItem>();

export const SelectCompany = <Fields extends Record<string, unknown>>({
  autoFocus,
  errors,
  excludeCompanyIds,
  label,
  onChange,
  onBlur,
  onKeyDown,
  selectedCompanyId,
  touched,
  placeholder,
}: SelectCompanyProps<Fields>) => {
  const companies: Company[] = useAppSelector(
    (state) => state.clients.activeCompanies,
  ).sort((a, b) => b.createdDate.localeCompare(a.createdDate));

  const companyOptions = companies
    .filter(
      (company) =>
        !excludeCompanyIds ||
        excludeCompanyIds.length === 0 ||
        !excludeCompanyIds.includes(company.id),
    )
    .map((company) => ({ company, label: company.fields.name ?? '' }));

  const isSelectedCompanyIdUuid = z
    .string()
    .uuid()
    .safeParse(selectedCompanyId).success;

  const selectedCompany = isSelectedCompanyIdUuid
    ? companies.find(({ id }) => id === selectedCompanyId)
    : undefined;
  const [value, setValue] = React.useState<CompanyAutocompleteItem | null>(
    null,
  );
  const [inputValue, setInputValue] = React.useState<string>('');

  React.useEffect(() => {
    if (selectedCompany) {
      setInputValue(selectedCompany.fields.name);
      setValue({
        company: selectedCompany,
        label: selectedCompany.fields.name,
      });
    } else if (selectedCompanyId && !isSelectedCompanyIdUuid) {
      setInputValue(selectedCompanyId);
      setValue({ company: undefined, label: selectedCompanyId });
    }
  }, [isSelectedCompanyIdUuid, selectedCompany, selectedCompanyId]);

  const groupOptionMapper = getGroupOptionMapper();

  return (
    <Autocomplete<CompanyAutocompleteItem, false, boolean, true>
      blurOnSelect
      disableClearable={!selectedCompanyId}
      filterOptions={(opts, params) => {
        const trimmedInputValue = params.inputValue.trim();
        let filtered = filter(opts, params);
        if (trimmedInputValue) {
          const exitingOption = filtered.find(
            (o) => o.label.toLowerCase() === params.inputValue.toLowerCase(),
          );

          filtered = [
            ...(!exitingOption
              ? [
                  {
                    label: params.inputValue,
                  },
                ]
              : []),
            ...filtered,
          ];
        }
        return filtered.slice(0, 6);
      }}
      freeSolo
      // option is a string when the user types into the input and hits enter.
      getOptionLabel={(option: string | CompanyAutocompleteItem) => {
        return typeof option === 'string' ? option : option.label;
      }}
      groupBy={(option) => (option.company ? groupOptionMapper.company : '')}
      inputValue={inputValue}
      ListboxComponent={
        GroupListboxComponent as React.ComponentType<
          React.HTMLAttributes<HTMLElement>
        >
      }
      onChange={(_, val) => {
        if (val === null) {
          setInputValue('');
        }
        const newValue = typeof val === 'string' ? { label: val } : val;
        onChange(newValue);
        setValue(newValue);
      }}
      options={companyOptions}
      renderInput={(params) => (
        <SelectCompanyTextInput
          {...params}
          autoFocus={autoFocus}
          errors={errors}
          inputValue={inputValue}
          isSelectedCompanyIdUuid={isSelectedCompanyIdUuid}
          label={label}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          selectedCompany={selectedCompany}
          selectedCompanyId={selectedCompanyId}
          setInputValue={setInputValue}
          setValue={setValue}
          touched={touched}
        />
      )}
      renderGroup={RenderGroup}
      renderOption={SelectCompanyAutocompleteItem}
      open={autoFocus && !touched}
      value={value}
    />
  );
};
